import React, { useEffect, useMemo, useState } from 'react';
import emailjs from 'emailjs-com';
import {
  RESTAURANT_PHONE_DISPLAY,
  RESTAURANT_WHATSAPP_DISPLAY,
  RESTAURANT_WHATSAPP_URL,
} from '../../constants/contact';
import './reservation.css';

export default function ReservationModal() {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    date: '',
    time: '',
    persons: 2,
    name: '',
    phone: '',
    email: '',
  });
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);
  const minDate = useMemo(() => new Date().toISOString().split('T')[0], []);

  useEffect(() => {
    const openHandler = () => setOpen(true);
    window.addEventListener('open-reservation', openHandler);
    return () => window.removeEventListener('open-reservation', openHandler);
  }, []);

  useEffect(() => {
    if (!open) {
      return undefined;
    }

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [open]);

  const onChange = (event) =>
    setForm((currentForm) => ({ ...currentForm, [event.target.name]: event.target.value }));

  const close = () => {
    setOpen(false);
    setDone(false);
    setSending(false);
  };

  async function submit(event) {
    event.preventDefault();
    setSending(true);

    const service = process.env.REACT_APP_EMAILJS_SERVICE_ID;
    const tplRest = process.env.REACT_APP_EMAILJS_TEMPLATE_RESTAURANT;
    const tplCust = process.env.REACT_APP_EMAILJS_TEMPLATE_CUSTOMER;
    const pubKey = process.env.REACT_APP_EMAILJS_PUBLIC_KEY;

    const payload = {
      ...form,
      persons: String(form.persons),
      created_at: new Date().toLocaleString(),
    };

    try {
      if (service && tplRest && tplCust && pubKey) {
        await emailjs.send(service, tplRest, payload, pubKey);
        await emailjs.send(service, tplCust, payload, pubKey);
        setDone(true);
      } else {
        alert(
          `Email service not configured. Please call ${RESTAURANT_PHONE_DISPLAY} or WhatsApp ${RESTAURANT_WHATSAPP_DISPLAY}.`
        );
      }
    } catch (error) {
      alert(`Error sending reservation. Please try again or call ${RESTAURANT_PHONE_DISPLAY}.`);
      console.error(error);
    } finally {
      setSending(false);
    }
  }

  if (!open) {
    return null;
  }

  return (
    <div className="rv-modal" role="dialog" aria-modal="true" aria-labelledby="reservation-title">
      <div className="rv-backdrop" onClick={close} />

      <div className="rv-card">
        <div className="rv-header">
          <div>
            <p className="rv-kicker">Reservations</p>
            <h3 id="reservation-title">Reserve your table</h3>
          </div>
          <button type="button" className="rv-x" onClick={close} aria-label="Close reservation">
            x
          </button>
        </div>

        {done ? (
          <div className="rv-done" aria-live="polite">
            <p>Your reservation request was sent. We will be in touch shortly.</p>
            <a href={RESTAURANT_WHATSAPP_URL} target="_blank" rel="noopener noreferrer">
              Prefer a faster reply? Message us on WhatsApp.
            </a>
            <button type="button" className="rv-btn" onClick={close}>
              Close
            </button>
          </div>
        ) : (
          <form className="rv-form" onSubmit={submit}>
            <label>
              Date
              <input
                type="date"
                name="date"
                min={minDate}
                value={form.date}
                onChange={onChange}
                required
              />
            </label>

            <label>
              Time
              <input type="time" name="time" value={form.time} onChange={onChange} required />
            </label>

            <label>
              Persons
              <input
                type="number"
                min="1"
                name="persons"
                value={form.persons}
                onChange={onChange}
                required
              />
            </label>

            <label>
              Name
              <input type="text" name="name" value={form.name} onChange={onChange} required />
            </label>

            <label>
              Phone
              <input type="tel" name="phone" value={form.phone} onChange={onChange} required />
            </label>

            <label>
              Email
              <input type="email" name="email" value={form.email} onChange={onChange} required />
            </label>

            <div className="rv-actions">
              <button type="submit" className="rv-btn" disabled={sending}>
                {sending ? 'Sending...' : 'Send reservation'}
              </button>

              <a
                className="rv-secondary-link"
                href={RESTAURANT_WHATSAPP_URL}
                target="_blank"
                rel="noopener noreferrer"
              >
                WhatsApp {RESTAURANT_WHATSAPP_DISPLAY}
              </a>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
